// earth/glsl.js: shaders for the earth world. One full-screen pass ray-traces
// the planet, cloud deck, atmosphere (single scattering, Chapman columns),
// airglow, Sun disk and stars; a cheap additive pass adds the sunburst and the
// pale-blue-dot point. Geometry in Earth radii, scattering in km.
import { GLSL_HASH } from '../lib/common.js';

export const FS_VERT = /* glsl */`
varying vec2 vUv;
void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`;

const COMMON = /* glsl */`
#define PI 3.14159265359
const float RE = 6371.0;
const float RA = 1.0 + 90.0 / 6371.0;   // top of the scattering shell
const float RC = 1.0 + 9.0 / 6371.0;    // cloud deck
const float HR = 8.0, HM = 1.2;         // scale heights, km
const vec3 BR = vec3(5.802e-3, 13.558e-3, 33.1e-3);        // Rayleigh /km (Hillaire 2020)
const vec3 BO = vec3(0.650e-3, 1.881e-3, 0.085e-3) * 2.5;  // ozone, folded into the Rayleigh column
const float BM = 3.996e-3, BMX = 4.40e-3;                  // Mie scattering / extinction /km
vec2 sphere(vec3 ro, vec3 rd, float r) {
  float b = dot(ro, rd);
  vec3 q = ro - b * rd;
  float h = r * r - dot(q, q);
  if (h < 0.0) return vec2(1e9, -1e9);
  h = sqrt(h);
  return vec2(-b - h, -b + h);
}
// column (km of sea-level air) from radius r (km) to space along cos-zenith mu (Chapman, Schueler)
float chap(float r, float mu, float H) {
  float c = sqrt(1.5707963 * r / H);
  float e = exp(clamp((RE - r) / H, -80.0, 60.0));
  if (mu >= 0.0) return H * e * c / ((c - 1.0) * mu + 1.0);
  float r0 = r * sqrt(max(1.0 - mu * mu, 0.0));
  float c0 = sqrt(1.5707963 * r0 / H);
  return H * (2.0 * c0 * exp(clamp((RE - r0) / H, -80.0, 60.0)) - e * c / ((c - 1.0) * (-mu) + 1.0));
}
vec3 trans(float r, float mu) { return exp(-((BR + BO) * chap(r, mu, HR) + BMX * chap(r, mu, HM))); }
vec2 dirUv(vec3 d) { return vec2(atan(d.x, d.z) * (0.5 / PI) + 0.5, asin(clamp(d.y, -1.0, 1.0)) * (1.0 / PI) + 0.5); }
`;

export const MAIN_FRAG = /* glsl */`
${COMMON}
${GLSL_HASH}
uniform vec3 uCamPos, uSun, uSunE, uCity;
uniform mat4 uInvProj, uCamWorld;
uniform sampler2D tDay, tNight, tClouds, tLand, tCity;
uniform vec4 uCityB;
uniform float uCityOn, uLights, uNightAmb, uAirglow, uClear, uSunDisk, uSunR, uPix, uDisk, uStars, uStarN, uRough, uAlb, uMulti;
uniform sampler2D tCityL;
uniform vec2 uCityKm;
uniform float uMoon, uNightW, uCityLOn, uAer, uHz, uWarmL, uCityLook;
varying vec2 vUv;

float ign(vec2 p) { return fract(52.9829189 * fract(dot(p, vec2(0.06711056, 0.00583715)))); }
float phaseR(float c) { return 0.0596831 * (1.0 + c * c); }
float phaseM(float c) { const float g = 0.8; float k = 1.0 + g * g - 2.0 * g * c; return 0.1193662 * (1.0 - g * g) * (1.0 + c * c) / ((2.0 + g * g) * k * sqrt(k)); }
float cov(float c) { return smoothstep(0.08, 0.85, c); }
// Catmull-Rom (9 bilinear taps) for the magnified 500 m city crop
float cityBicubic(vec2 uv) {
  vec2 texel = vec2(1.0 / 1024.0), sp = uv / texel, t1 = floor(sp - 0.5) + 0.5, f = sp - t1;
  vec2 w0 = f * (-0.5 + f * (1.0 - 0.5 * f)), w1 = 1.0 + f * f * (-2.5 + 1.5 * f);
  vec2 w2 = f * (0.5 + f * (2.0 - 1.5 * f)), w3 = f * f * (-0.5 + 0.5 * f), w12 = w1 + w2;
  vec2 p0 = (t1 - 1.0) * texel, p3 = (t1 + 2.0) * texel, p12 = (t1 + w2 / w12) * texel;
  float r = textureLod(tCity, vec2(p0.x, p0.y), 0.0).r * w0.x * w0.y + textureLod(tCity, vec2(p12.x, p0.y), 0.0).r * w12.x * w0.y
          + textureLod(tCity, vec2(p3.x, p0.y), 0.0).r * w3.x * w0.y + textureLod(tCity, vec2(p0.x, p12.y), 0.0).r * w0.x * w12.y
          + textureLod(tCity, vec2(p12.x, p12.y), 0.0).r * w12.x * w12.y + textureLod(tCity, vec2(p3.x, p12.y), 0.0).r * w3.x * w12.y
          + textureLod(tCity, vec2(p0.x, p3.y), 0.0).r * w0.x * w3.y + textureLod(tCity, vec2(p12.x, p3.y), 0.0).r * w12.x * w3.y
          + textureLod(tCity, vec2(p3.x, p3.y), 0.0).r * w3.x * w3.y;
  return clamp(r, 0.0, 1.0);
}

float vnoise(vec2 x) {
  vec2 i = floor(x), f = fract(x); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash12(i), hash12(i + vec2(1.0, 0.0)), f.x), mix(hash12(i + vec2(0.0, 1.0)), hash12(i + 1.0), f.x), f.y);
}

// ---- ARTISTIC sub-texel light structure. Every pattern has mean ~1, so the NASA radiance is kept
// on average and only redistributed into towns, streets and lamps finer than the data can show.
float lineAA(float d, float w, float fw) { float h = max(w, 0.7 * fw); return clamp(1.0 - d / h, 0.0, 1.0) * (w / h); }
float town1(vec2 q, float S, float fw, float seed) {
  vec2 g = q / S, c = floor(g);
  vec3 h = hash33(vec3(c, seed));
  vec2 o = (g - c - 0.28 - 0.44 * h.xy) * S;
  float r = S * mix(0.05, 0.18, h.z * h.z), rr = max(r, 0.5 * fw);
  float wt = step(0.3, hash12(c * 1.37 + seed)) * (0.3 + 1.4 * hash12(c * 0.71 + 3.3 + seed)) / 0.7;
  return min(wt * exp(-dot(o, o) / (rr * rr)) * (S * S) / (PI * rr * rr), 6.0);
}
float townTex(vec2 q, float fw) {            // q: km, fw: km per pixel. Global 3 km map -> towns (warped, 2 scales)
  float k = 1.0 - smoothstep(0.18, 0.45, fw / 5.0);
  if (k <= 0.0) return 1.0;
  q += 3.0 * (vec2(vnoise(q * 0.07 + 1.7), vnoise(q * 0.07 + 8.3)) - 0.5);
  float k2 = 1.0 - smoothstep(0.18, 0.45, fw / 2.1), t = town1(q, 5.0, fw, 3.0);
  t = mix(t, 0.55 * t + 0.45 * town1(q + 0.9, 2.1, fw, 9.0), k2);
  return mix(1.0, 0.3 + 0.7 * t, k);
}
float lampTex(vec2 g, float SL, float fw, float seed) {   // one lamp cluster per SL km cell, 60% lit; mean 1
  float k = 1.0 - smoothstep(0.25, 0.55, fw / SL);
  if (k <= 0.0) return 1.0;
  vec2 gl = g / SL, li = floor(gl);
  vec3 lh = hash33(vec3(li, seed));
  vec2 lo = (gl - li - 0.2 - 0.6 * lh.xy) * SL;
  float rr = max(0.1 * SL, 0.5 * fw);
  return mix(1.0, min(step(0.4, lh.z) / 0.6 * exp(-dot(lo, lo) / (rr * rr)) * (SL * SL) / (PI * rr * rr), 12.0), k);
}
// ---- r7 city close-up (ARTISTIC; uCityLook = 1 over p .47-.64, 0 on the night side and the gibbous Earth): knobs
const vec4 R7A = vec4(0.02, 0.07, 0.62, 1.0);   // street base fill lo/hi (r6 .05/.25), lamp weight (r6 .38), sodium share of arterial light
const vec4 R7B = vec4(0.60, 0.12, 7.0, 1.5);    // sodium spark threshold, LED fringe -> white-hot core over I lo/hi, white-hot boost (r6 .6)
const vec4 R7C = vec4(3.0, 8.0, 0.28, 0.18);    // sodium core -> warm white over I lo/hi, share of districts with sodium arterials, fine-glow gain
const vec4 R7S = vec4(0.03073, -0.994, 0.01365, 0.3416);   // sodium g/r = x * I^y in [z, w]: pre-compensates the tone curve (bright orange drifts to gold)
const vec4 R7D = vec4(0.8, 0.6, 0.8, 0.0);     // fine-glow LOD (crop texels 2^x), glow cut in sodium districts, airglow cut in the city look, -
const vec3 M7_LAND = vec3(0.602, 0.582, 0.871);      // moonlit land in the 500 m crop: near-black, low-sat navy (extra red survives the grade's black crush + sat)
const vec3 M7_OUT = vec3(0.514, 0.55, 1.007);   // moonlit land outside the crop (brighter albedo map): plain navy, never violet
const vec3 M7_SEA = vec3(0.09, 0.80, 1.40);     // faint cool sheen on the sea
float gArt = 0.0, gSodD = 0.0, gSodPx = 0.0;   // set by streetTex / cityTint: arterial share of the street light, sodium district, sodium share
float streetTex(vec2 q, float fw, float hot, float dn) {   // q: km in the city crop. arterials + street grid + lamp clusters, mean ~1
  vec2 wq = q + 1.6 * (vec2(vnoise(q * 0.21 + 3.1), vnoise(q * 0.21 + 17.7)) - 0.5)
              + 0.25 * (vec2(vnoise(q * 0.93 + 9.2), vnoise(q * 0.93 + 1.3)) - 0.5);
  vec2 cid = floor(wq / 3.1);
  vec3 h = hash33(vec3(cid, 11.0));
  gSodD = step(1.0 - R7C.z, fract(h.z * 7.13));   // r7: R7C.z of the districts light their arterials sodium
  float a = h.x * 1.5707963, s = mix(0.42, 0.75, h.y);        // district: grid angle, block size (km)
  vec2 cs = vec2(cos(a), sin(a)), ci = cid * 17.0;
  vec2 g = vec2(dot(wq, cs), dot(wq, vec2(-cs.y, cs.x)));
  const float WA = 0.05, WR = 0.025;
  float sa = 3.0 * s;
  float kA = 1.0 - smoothstep(0.12, 0.3, fw / sa);
  if (kA <= 0.0) return 1.0;
  vec2 ga = g / sa, da = (0.5 - abs(fract(ga) - 0.5)) * sa;    // arterials every 3 blocks, 70% lit
  float art = (lineAA(da.x, WA, fw) * step(0.3, hash12(vec2(floor(ga.x + 0.5), 1.0) + ci)) * (0.4 + 1.2 * hash12(vec2(floor(ga.x + 0.5), 4.0) + ci))
             + lineAA(da.y, WA, fw) * step(0.3, hash12(vec2(floor(ga.y + 0.5), 2.0) + ci)) * (0.4 + 1.2 * hash12(vec2(floor(ga.y + 0.5), 6.0) + ci))) * sa / (1.4 * WA);
  float kR = 1.0 - smoothstep(0.22, 0.5, fw / s), minor = 1.0;
  if (kR > 0.0) {
    vec2 gs = g / s, d = (0.5 - abs(fract(gs) - 0.5)) * s;
    minor = (lineAA(d.x, WR, fw) * (0.15 + 1.7 * hash12(vec2(floor(gs.x + 0.5), floor(gs.y)) + ci))
           + lineAA(d.y, WR, fw) * (0.15 + 1.7 * hash12(vec2(floor(gs.x), floor(gs.y + 0.5)) + ci + 5.3))) * s / (2.0 * WR);
  }
  float lamps = lampTex(g, 0.7, fw, 5.0 + h.z), wm = 0.2 * kR * smoothstep(0.06, 0.4, dn);   // suburbs: lamps + arterials only   // unresolved layers hand their light to the others
  float wL = mix(0.38, R7A.z, uCityLook), aL = 0.42 * art, tL = aL + wm * minor + wL * lamps;
  gArt = kA * aL / max(tL, 1e-4) * gSodD;
  float det = tL / (0.42 + wL + wm);
  float base = mix(mix(0.05, R7A.x, uCityLook), mix(0.25, R7A.y, uCityLook), hot);   // r7: less fill = crisp grid, no haze
  return mix(1.0, base + (1.0 - base) * det, kA);
}

// ---- ARTISTIC light colour (LIGHT.md HOME, 'representative colour'): mostly LED white #EAF4FF and cyan-white
// #9EE7FF; sodium #FF9F43 only in a minority of districts + warm sparks (<= 14% of lit px measured). Unit luminance, so the NASA
// brightness is unchanged. q: km on the ground, fw: km per pixel (unresolved noise -> its mean, no shimmer).
const vec3 C_LED = vec3(0.823, 0.905, 1.0) / 0.894, C_CYA = vec3(0.20, 0.84, 1.0) / 0.716, C_SOD = vec3(1.0, 0.347, 0.056) / 0.465;
const vec3 LIMB_T = vec3(0.86, 1.5, 0.7);   // ARTISTIC: unreddened Rayleigh light -> the #37E0FF cyan limb
vec3 lightTint(vec2 q, float fw, float nD, float hot, float ci) {   // ci: inside the 500 m city crop
  float kd = 1.0 - smoothstep(0.4, 1.0, fw / 9.0), kb = 1.0 - smoothstep(0.4, 1.0, fw / 0.6), kc = 1.0 - smoothstep(0.4, 1.0, fw / 4.3);
  float dn = 0.65 * vnoise(q * 0.11 + 7.3) + 0.35 * vnoise(q * 0.37 + 2.1);
  float sod = mix(0.08, smoothstep(0.73, 0.78, dn), kd);                          // sodium districts
  float t0 = mix(0.68, 0.585, ci);   // a few more warm sparks in the city close-up (still well under 25% of the lit area)
  sod = max(sod, mix(0.02, smoothstep(t0, t0 + 0.06, vnoise(q * 1.6 + 4.1)), kb));    // + single warm sparks
  sod *= 1.0 - hot;                                                              // the densest cores are LED
  float cy = mix(0.55, smoothstep(0.2, 0.7, vnoise(q * 0.23 + 11.9)), kc) * (1.0 - 0.45 * smoothstep(0.4, 0.9, nD));
  return mix(mix(C_LED, C_CYA, cy), C_SOD, sod);
}
// r7 city close-up colour (ARTISTIC, 'representative colour'): each LED light runs from a coloured fringe to a white-hot core by
// its own brightness I: LED #EAF4FF (azure fringe), cyan-white #9EE7FF (cyan fringe). Warm minority = sodium amber (20-23 deg on
// screen, a touch deeper than #FF9F43 so it never reads gold) on the arterials of R7C.z of the districts + single sparks, warm white at its hottest.
const vec3 K_LEDF = vec3(0.100, 0.450, 1.000) / 0.4153, K_CYA = vec3(0.342, 0.799, 1.0) / 0.7164, K_CYAF = vec3(0.000, 0.850, 1.000) / 0.6801;
const vec3 C_WW = vec3(0.95, 0.93, 1.0) / 0.9393;   // sodium core -> neutral white (a warm white drifted to khaki mud through ACES)
vec3 cityTint(vec2 q, float fw, float nD, float hot, float I) {
  float kb = 1.0 - smoothstep(0.4, 1.0, fw / 0.6), kc = 1.0 - smoothstep(0.4, 1.0, fw / 2.2);
  float cy = mix(0.55, smoothstep(0.25, 0.75, vnoise(q * 0.45 + 11.9)), kc) * (1.0 - 0.3 * smoothstep(0.5, 0.95, nD));
  // v5.1: colour by DISTRICT (km-scale patches, like real cities from orbit), never per pixel: per-pixel sodium arterials + sparks
  // between white LED streets read as RGB speckle. Whole sodium districts (R7C.z of them); the hot cores stay LED.
  float dn = 0.65 * vnoise(q * 0.11 + 7.3) + 0.35 * vnoise(q * 0.37 + 2.1);   // organic ~3-9 km districts with soft edges (square cells read as tiles)
  float sod = smoothstep(0.60, 0.68, dn) * R7A.w * (1.0 - smoothstep(0.2, 0.5, hot)) + 0.0 * kb;
  gSodPx = sod;
  float w = max(max(smoothstep(R7B.y, R7B.z, I), 0.85 * hot), 0.6);      // LED: coloured fringe -> white-hot core; fringe capped at 40% (v5.1: per-pixel fringe/core flips read as RGB speckle)
  float gr = clamp(R7S.x * pow(max(I, 1e-3), R7S.y), R7S.z, R7S.w);      // sodium g/r fitted to ACES + sRGB + sat: ~23 deg on screen at any I
  vec3 cs = mix(vec3(1.0, gr, 0.1 * gr) / (0.2126 + 0.72242 * gr), C_WW, smoothstep(R7C.x, R7C.y, I));   // sodium; neutral white at its hottest
  vec3 c = mix(mix(mix(K_LEDF, C_LED, w), mix(K_CYAF, K_CYA, w), cy), cs, sod);
  return mix(c, C_LED, 1.6 * min(sod, 1.0 - sod));   // half-sodium px (mask edge / unresolved) -> LED white: orange + azure would read pink
}

vec3 airglow(vec3 ro, vec3 rd, float bq, vec3 qc, bool hitG) {
  const float RAG = 1.0 + 97.0 / 6371.0, WAG = 3.0 / 6371.0;   // OI 557.7 nm layer
  float rho = length(qc);
  if (bq > 0.0 || rho > RAG + 3.0 * WAG) return vec3(0.0);
  vec3 pa = hitG ? normalize(ro + rd * (-bq - sqrt(max(RAG * RAG - rho * rho, 0.0)))) : qc / max(rho, 1e-6);
  float night = smoothstep(-0.03, -0.22, dot(pa, uSun)) * (hitG ? 0.3 : 1.0);
  if (night < 1e-3) return vec3(0.0);
  float u0 = sqrt(max(RAG - 3.0 * WAG - rho, 0.0)), u1 = sqrt(RAG + 3.0 * WAG - rho);
  float du = (u1 - u0) / float(AGN), s = 0.0;
  for (int i = 0; i < AGN; i++) {       // r = rho + u^2 removes the limb singularity
    float uu = u0 + (float(i) + 0.5) * du, r = rho + uu * uu;
    float z = (r - RAG) / WAG;
    s += exp(-z * z) * 2.0 * r / sqrt(r + rho);
  }
  s *= du * (hitG ? 1.0 : 2.0) * RE;
  const float PK = 1.813 * sqrt(2.0 * RAG * RE * WAG * RE);   // tangent column at the layer peak (km)
  float sn = s / PK;
  // ARTISTIC shaping: keep the edge-on peak, suppress the below-tangent wash (reads as the thin ISS line)
  return uAirglow * night * PK * sn * sn * sqrt(sn) * mix(vec3(0.10, 0.52, 0.62), vec3(0.20, 1.0, 0.43), smoothstep(0.35, 0.9, sn)) * (1.0 - smoothstep(1.12, 1.6, length(ro)));
}

vec3 starField(vec3 d) {
  vec3 a = abs(d); vec2 f; float id;
  if (a.x >= a.y && a.x >= a.z) { f = d.yz / a.x; id = sign(d.x); }
  else if (a.y >= a.z) { f = d.xz / a.y; id = 2.0 + sign(d.y); }
  else { f = d.xy / a.z; id = 5.0 + sign(d.z); }
  vec2 g = (f * 0.5 + 0.5) * uStarN, c = floor(g);
  vec3 h = hash33(vec3(c, id * 17.0));
  vec2 q = (g - c - 0.2 - 0.6 * h.xy) * (2.0 / uStarN) / (1.0 + dot(f, f));
  float m = step(0.986, h.z) * (pow((h.z - 0.986) / 0.014, 4.0) * 5.0 + 0.25);
  float s = uPix * 0.62;
  vec3 tint = mix(vec3(1.0, 0.82, 0.66), vec3(0.72, 0.84, 1.0), hash12(c + id));
  return tint * m * exp(-dot(q, q) / (2.0 * s * s));
}

void main() {
  vec2 ndc = vUv * 2.0 - 1.0;
  vec4 vv = uInvProj * vec4(ndc, -1.0, 1.0);
  vec3 rd = normalize(mat3(uCamWorld) * (vv.xyz / vv.w)), ro = uCamPos;
  vec2 tg = sphere(ro, rd, 1.0), ta = sphere(ro, rd, RA), tc = sphere(ro, rd, RC);
  bool hitG = tg.x < tg.y && tg.x > 0.0;
  float bq = dot(ro, rd);
  vec3 qc = ro - bq * rd;
  // derivatives in uniform control flow (dateline-wrapped)
  vec3 ng = hitG ? normalize(ro + rd * tg.x) : normalize(qc);
  vec3 nc = (tc.x < tc.y && tc.x > 0.0) ? normalize(ro + rd * tc.x) : ng;
  vec2 uvG = dirUv(ng), uvC = dirUv(nc);
  vec2 gx = dFdx(uvG), gy = dFdy(uvG); gx.x -= floor(gx.x + 0.5); gy.x -= floor(gy.x + 0.5);
  vec2 cx = dFdx(uvC), cy = dFdy(uvC); cx.x -= floor(cx.x + 0.5); cy.x -= floor(cy.x + 0.5);
  vec2 ll = vec2((uvG.x - 0.5) * 2.0 * PI, (uvG.y - 0.5) * PI);
  vec2 cuv = vec2((ll.x - uCityB.z) / (uCityB.w - uCityB.z), (ll.y - uCityB.x) / (uCityB.y - uCityB.x));
  vec2 kx = dFdx(cuv), ky = dFdy(cuv);
  float cl0 = cos(ll.y);
  vec2 qG = vec2(ll.x * cl0, ll.y) * RE, scG = vec2(2.0 * PI * cl0, PI) * RE;
  float fwG = max(length(gx * scG), length(gy * scG)), fwC = max(length(kx * uCityKm), length(ky * uCityKm));

  float mu = dot(rd, uSun);
  vec3 col = vec3(0.0), Tv = vec3(1.0);
  if (ta.x < ta.y && ta.y > 0.0) {
    float t0 = max(ta.x, 0.0), t1 = hitG ? tg.x : ta.y;
    float tm = hitG ? t1 : clamp(-bq, t0, t1), jit = ign(gl_FragCoord.xy);
    vec3 aR = vec3(0.0), aM = vec3(0.0), aA = vec3(0.0);
    float limbK = hitG ? 1.0 - smoothstep(0.0, 0.3, dot(ng, -rd)) : 1.0;   // cyan only toward the limb, not the day disk
    bool dark = hitG && dot(ng, uSun) < -0.2 && dot(normalize(ro + rd * t0), uSun) < -0.2;   // path inside Earth's shadow
    for (int i = 0; i < STEPS; i++) {
      if (dark) break;   // samples packed towards the ground / tangent point
      float u = (float(i) + jit) / float(STEPS), t, dt;
      if (hitG) { float s = 1.0 - u; t = t1 - (t1 - t0) * s * s; dt = 2.0 * (t1 - t0) * s; }
      else if (u < 0.5) { float s = 1.0 - 2.0 * u; t = tm - (tm - t0) * s * s; dt = 4.0 * (tm - t0) * s; }
      else { float s = 2.0 * u - 1.0; t = tm + (t1 - tm) * s * s; dt = 4.0 * (t1 - tm) * s; }
      vec3 x = ro + rd * t;
      float r = length(x); vec3 xn = x / r; float rk = r * RE, h = rk - RE;
      vec3 Ts = trans(rk, dot(xn, uSun));
      Ts = mix(Ts, vec3(dot(Ts, vec3(0.2126, 0.7152, 0.0722))) * vec3(0.5, 0.95, 1.3) * 0.6, (1.0 - smoothstep(0.3, 0.8, Ts.b)) * (1.0 - uWarmL));   // red-orange limb line at the sunrise only (LIGHT.md)
      vec3 w = trans(rk, -dot(xn, rd)) * Ts * (dt * RE / float(STEPS));
      aR += exp(-h / HR) * w * mix(vec3(1.0), LIMB_T, smoothstep(0.3, 0.8, Ts.b) * limbK); aM += exp(-h / HM) * w; aA += exp(-h / 6.0) * w;
    }
    vec3 ins = uSunE * (BR * aR * (phaseR(mu) + uMulti) + (BM * aM + uAer * aA) * phaseM(mu));   // uAer: thin free-tropospheric aerosol (artistic), white-gold sunrise band
    if (hitG) {
      vec3 v = -rd;
      float mus = dot(ng, uSun);
      Tv = trans(RE, dot(ng, v));
      vec3 Tg = trans(RE, mus);
      float cl = cov(textureGrad(tClouds, uvC, cx, cy).r) * smoothstep(uClear * 0.45, uClear, length(nc - uCity));
      vec3 ps = normalize(ng + uSun * (-mus + sqrt(mus * mus - 1.0 + RC * RC)));
      float csh = cov(textureGrad(tClouds, dirUv(ps), cx, cy).r) * smoothstep(uClear * 0.45, uClear, length(ps - uCity));
      vec3 alb = textureGrad(tDay, uvG, gx, gy).rgb * uAlb;
      float water = 1.0 - smoothstep(0.03, 0.2, textureGrad(tLand, uvG, gx, gy).r);
      vec3 E = uSunE * Tg * (1.0 - 0.8 * csh);
      vec3 sky = uSunE * vec3(0.020, 0.036, 0.08) * smoothstep(-0.1, 0.5, mus) + uSunE * Tg * vec3(0.05, 0.016, 0.012) * smoothstep(-0.08, 0.0, mus) * smoothstep(0.12, 0.01, mus);
      vec3 surf = alb * (E * max(mus, 0.0) / PI + sky);
      vec3 hv = normalize(uSun + v);   // GGX sun glint on the ocean
      float nh = max(dot(ng, hv), 0.0), nv = max(dot(ng, v), 1e-4), nl = max(mus, 0.0), vh = max(dot(v, hv), 0.0), a2 = uRough;
      float dd = nh * nh * (a2 - 1.0) + 1.0;
      float spec = a2 / (PI * dd * dd) * (0.02 + 0.98 * pow(1.0 - vh, 5.0)) * 0.5 / (nl * sqrt(nv * nv * (1.0 - a2) + a2) + nv * sqrt(nl * nl * (1.0 - a2) + a2) + 1e-5);
      surf += E * spec * nl * water;
      // ---- night lights. REAL: NASA Black Marble 2016 (global 3 km map; 500 m crop at the city).
      // ARTISTIC: towns / streets / lamps below the data's resolution (mean kept) and the light colour (LED / cyan-white, sodium minority).
      float nightM = smoothstep(0.03, -0.12, mus);
      float n0 = textureGrad(tNight, uvG, gx, gy).r, nlt = n0;
      float lodN = log2(max(max(length(gx), length(gy)) * uNightW, 1e-6));
      if (nightM > 0.0 && lodN < 0.5)   // unsharp while magnified: blotches -> defined clusters
        nlt = clamp(n0 + 0.9 * (1.0 - smoothstep(-0.5, 0.5, lodN)) * (n0 - textureLod(tNight, uvG, max(lodN + 1.2, 1.2)).r), 0.0, 1.0);
      else if (nightM > 0.0 && uHz > 0.0) {   // toward the horizon the minor axis is still magnified: anisotropic unsharp (same footprint x2.5)
        float lodM = log2(max(min(length(gx), length(gy)) * uNightW, 1e-6));
        if (lodM < 0.5) nlt = clamp(n0 + uHz * 0.9 * (1.0 - smoothstep(-0.5, 0.5, lodM)) * smoothstep(0.5, 1.5, lodN) * (n0 - textureGrad(tNight, uvG, gx * 2.5, gy * 2.5).r), 0.0, 1.0);
      }
      float inC = uCityOn * smoothstep(0.0, 0.1, cuv.x) * smoothstep(1.0, 0.9, cuv.x) * smoothstep(0.0, 0.1, cuv.y) * smoothstep(1.0, 0.9, cuv.y);
      float Lm = nlt * nlt, L = Lm, nD = nlt, hot = 0.0, mt = 0.0;
      if (nightM > 0.0 && inC < 1.0) L = Lm * townTex(qG, fwG);
      if (inC > 0.0) {
        vec2 cc = clamp(cuv, 0.0, 1.0);
        float tpx = (1.0 / 1024.0) / max(max(length(kx), length(ky)), 1e-9);   // screen px per crop texel
        float ncl = textureGrad(tCity, cc, kx, ky).r;
        if (tpx > 1.0) ncl = mix(ncl, cityBicubic(cc), smoothstep(1.0, 1.6, tpx));
        float dens = mix(textureLod(tCity, cc, 4.0).r, textureLod(tCity, cc, 6.0).r, 0.5);   // ~16-60 km density dome
        hot = inC * smoothstep(0.62, 0.92, dens) * smoothstep(0.62, 0.95, textureLod(tCity, cc, 1.5).r);           // white-hot: densest districts only
        float Lc = ncl * ncl * (1.0 + mix(0.6, R7B.w, uCityLook) * hot);   // r7: brighter white-hot cores
        Lm = mix(Lm, Lc, inC);
        if (nightM > 0.0) Lc *= streetTex(cuv * uCityKm, fwC, hot, dens);
        L = mix(L, Lc, inC); nD = mix(nlt, ncl, inC);
        mt = textureGrad(tCityL, cc, kx, ky).r;
      }
      vec3 lc = vec3(0.0);
      if (nightM > 0.0) {   // colour ARTISTIC (lightTint); luminance = the old tuned sodium ramp, so the NASA brightness is kept
        vec3 lc0 = mix(mix(vec3(1.0, 0.40, 0.09), vec3(1.0, 0.55, 0.19), smoothstep(0.35, 0.85, nD)), vec3(1.0, 0.78, 0.52), hot * 0.8);
        lc = lightTint(qG, fwG, nD, hot, inC) * dot(lc0, vec3(0.2126, 0.7152, 0.0722));
      }
      vec3 lights = lc * L * uLights * nightM;
      float kCL = uCityLook * inC;
      if (kCL > 0.0 && nightM > 0.0) {   // r7 city close-up: white-hot cores over a fine coloured fringe (cityTint; luminance kept)
        float I = dot(lights, vec3(0.2126, 0.7152, 0.0722));
        lights = mix(lights, cityTint(qG, fwG, nD, hot, I) * I, kCL);
        float gd = textureLod(tCity, clamp(cuv, 0.0, 1.0), R7D.x).r, cg = smoothstep(0.25, 0.75, vnoise(qG * 0.45 + 11.9));
        lights += (kCL * R7C.w * uLights * nightM * gd * gd * (1.0 - 0.85 * gSodPx) * (1.0 - R7D.y * gSodPx)) * mix(C_LED, K_CYA, cg);   // ARTISTIC fine glow (~1.3 km, low-air scatter): LED/cyan only (a warm glow under cyan points reads grey)
      }
      // moonlight (ARTISTIC level): faint land albedo + relief, a near-black sea
      float moonK = uMoon * (1.0 - smoothstep(-0.25, 0.05, mus));
      float mG = dot(alb, vec3(0.3, 0.55, 0.15)) * 3.5 * (1.0 - water) + 0.03 * water;
      vec3 moonC = mix(mix(vec3(0.14, 0.47, 1.0) * 2.2, M7_OUT, uCityLook), M7_LAND, uCityLook * inC * uCityLOn);   // LIGHT.md moonlit land #0E2A52 (navy); r7 city: near-black navy
      float mL = mix(mG, mt, inC * uCityLOn);
      if (uCityLook > 0.0) {   // r7 (ARTISTIC): flatter relief (land barely readable) + a faint cool sheen on the sea in the city crop
        float lk = smoothstep(0.03, 0.15, mt), kC = uCityLook * inC * uCityLOn;
        mL = mix(mL, lk * mix(0.5, mt, 0.55), kC);
        surf += M7_SEA * (moonK * kC * (1.0 - lk) * (0.15 + 0.6 * vnoise(cuv * 3.0 + 5.3) + 0.25 * vnoise(cuv * 9.0 + 1.7)));
      }
      surf += lights + moonC * moonK * mL;
      float musc = dot(nc, uSun);
      vec3 cloud = uSunE * pow(trans(RC * RE, max(musc, -0.03)), vec3(0.7)) * (0.95 / PI) * (max(musc, 0.0) * 0.9 + 0.1 * smoothstep(0.0, 0.25, musc))
                 + 1.2 * sky + vec3(0.25, 0.45, 1.0) * moonK * mix(0.65, 0.3, uCityLook) + lc * Lm * uLights * nightM * 0.12;
      col = ins + Tv * mix(surf, cloud, cl * 0.96);
    } else {
      Tv = trans(RA * RE, dot(normalize(ro + rd * ta.y), -rd));
      col = ins;
    }
  }
  if (!hitG && mu > 0.0) {   // Sun disk with limb darkening, dimmed/reddened by the air it grazes
    float ang = asin(min(length(cross(rd, uSun)), 1.0));
    float r = clamp(ang / uSunR, 0.0, 1.0);
    col += uSunDisk * Tv * (0.45 + 0.55 * sqrt(max(1.0 - r * r, 0.0))) * smoothstep(uSunR + uPix, uSunR - uPix, ang);
  }
  col += airglow(ro, rd, bq, qc, hitG) * (1.0 - R7D.z * uCityLook);   // r7: its faint nadir green tinted the city's sea
  col *= uDisk;
  if (!hitG) col += uStars * Tv * starField(rd);
  gl_FragColor = vec4(col, 1.0);
}
`;

// additive full-res pass: camera glare / starburst around the Sun, and the
// pale-blue-dot point once the planet is smaller than a pixel.
export const FX_FRAG = /* glsl */`
uniform vec2 uRes, uSunPx, uDotPx;
uniform vec3 uGlareC, uDotC;
uniform float uGlare, uDot, uDotS, uRot, uVeil, uBand;
varying vec2 vUv;
float h11(float p) { p = fract(p * 0.1031); p *= p + 33.33; p *= p + p; return fract(p); }
float streaks(float a, float n, float seed) {
  float x = (a / 6.2831853 + 0.5) * n, i = floor(x), f = fract(x);
  return mix(h11(i + seed), h11(mod(i + 1.0, n) + seed), f * f * (3.0 - 2.0 * f));
}
void main() {
  vec2 p = vUv * uRes;
  vec3 c = vec3(0.0);
  vec2 d = (p - uSunPx) / uRes.y;
  float r = length(d) + 1e-5;
  if (uGlare > 1e-4) {
    float a = atan(d.y, d.x) + uRot;
    float core = exp(-r * r / 0.00008) * 6.0 + 0.7 / (1.0 + r * r / 0.0002);
    float halo = 0.07 * exp(-r * 8.0) + 0.012 * exp(-r * 2.2);
    float sp = pow(abs(cos(a * 3.0)), 320.0) * (0.8 * exp(-r * 12.0) + 0.02 * exp(-r * 4.0));
    sp += pow(abs(cos(a * 3.0 + 0.5236)), 900.0) * 0.3 * exp(-r * 14.0);
    float st = pow(streaks(a, 97.0, 3.1), 7.0) * 0.25 * exp(-r * 12.0) + pow(streaks(a, 211.0, 7.7), 12.0) * 0.2 * exp(-r * 18.0);
    c += uGlare * (uGlareC * core + vec3(0.72, 0.86, 1.0) * (halo + sp + st));   // white Sun, cool halo (LIGHT.md)
  }
  if (uDot > 1e-5) {
    vec2 dd = p - uDotPx;
    float q = dot(dd, dd) / (2.0 * uDotS * uDotS);
    // min-size point: pale blue-white core (~1.5 px), soft halo, faint wide glow
    c += uDot * (uDotC * exp(-q) + vec3(0.30, 0.56, 1.0) * (0.07 * exp(-q / 30.0) + 0.012 * exp(-q / 400.0)));
    // homage to Voyager 1's frame: a faint band of scattered sunlight through the dot
    float x = dot(dd / uRes.y, vec2(0.985, 0.174));
    float band = exp(-x * x / 0.0016) * (0.8 + 0.2 * sin(dd.y / uRes.y * 9.0));
    c += uBand * band * mix(vec3(0.62, 0.52, 1.0), vec3(0.40, 0.66, 1.0), smoothstep(-0.05, 0.05, x)) * 0.022;
  }
  c += vec3(0.86, 0.93, 1.0) * uVeil * (0.25 + 3.0 * exp(-r * 2.5));
  gl_FragColor = vec4(c, 1.0);
}
`;
